import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { CSSPropertiesWithVars } from '../../../services/TypeUtils';
import UnreadBadge from '../../Common/UnreadBadge';
import Icon, { type IconProps } from '../NewIcon';
import ProgressMessage from '../ProgressMessage';
import Button from '@material-ui/core/Button';

const ICON_STYLE = { position: 'relative', placeItems: 'center' } as const;

export type DefaultButtonProps = {
  icon?: IconProps['src'];
  fontAwesome?: IconProps['fontAwesomeStyle'];
  hide?: boolean;
  loading?: boolean;
  unreadCount?: number;
  buttonRef?: React.RefObject<HTMLButtonElement>;
} & React.HTMLProps<HTMLButtonElement>;

const DefaultButton = ({
  name,
  icon,
  fontAwesome,
  alt,
  onClick,
  style = {},
  hide = false,
  loading = false,
  disabled = false,
  className = 'white',
  unreadCount = 0,
  autoFocus,
  ...props
}: DefaultButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (loading) className += ' loading';

  useEffect(() => {
    //workaround for MUI v4 bug
    if (autoFocus && !hide) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 100);
    }
  }, [autoFocus, hide]);

  if (hide) {
    return null;
  }

  return (
    //@ts-expect-error
    <Button
      ref={props.buttonRef || buttonRef}
      variant="contained"
      key={name}
      id={props.id || `${name}-button`.replace(/\s/g, '-')}
      type="submit"
      disabled={loading || disabled}
      style={{ borderRadius: '25px', ...style }}
      className={`default-button ${className}`}
      classes={{ disabled: 'button-disabled' }}
      onClick={onClick}
      {...props}
      autoFocus={false}
    >
      {loading && <ProgressMessage />}
      {icon !== undefined ? (
        <>
          <Icon
            src={icon}
            fontAwesomeStyle={fontAwesome}
            width="15px"
            height="15px"
            style={ICON_STYLE}
            alt={alt}
            showTooltip={!!alt}
          />
          {!!name && <>&nbsp;</>}
        </>
      ) : null}
      <span>{name}</span>
      {unreadCount > 0 && (
        <div className="ml-1">
          <UnreadBadge className="ml-2" unreadCount={unreadCount} />
        </div>
      )}
    </Button>
  );
};

export default DefaultButton;
